import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Grid,
  Box,
  TextField,
  Button,
  Card,
  CardContent,
  Divider,
  Fade,
  Slide
} from "@mui/material";
import {
  Send,
  LocationOn,
  Email,
  Phone,
  Language,
  Business
} from "@mui/icons-material";
import { keyframes } from "@emotion/react";
import emailjs from "@emailjs/browser";

/* subtle corporate pulse */
const softPulse = keyframes`
  0% { box-shadow: 0 0 0 rgba(139,0,0,0.25); }
  50% { box-shadow: 0 0 18px rgba(139,0,0,0.45); }
  100% { box-shadow: 0 0 0 rgba(139,0,0,0.25); }
`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: ""
  });

  const [visible, setVisible] = useState(false);
  useEffect(() => setVisible(true), []);

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();

    emailjs
      .send(
        "zolang_nexora",
        "template_zolang_contact",
        formData,
        "TraCoJx_h__B6CoRO"
      )
      .then(() => {
        alert("✅ Message sent successfully");
        setFormData({ name: "", email: "", message: "" });
      })
      .catch(() => alert("❌ Failed to send message"));
  };

  return (
    <Container maxWidth="lg" sx={{ py: 10 }}>
      {/* Header */}
      <Fade in={visible} timeout={800}>
        <Box textAlign="center" mb={6}>
          <Typography
            variant="h3"
            fontWeight={800}
            sx={{
              background:
                "linear-gradient(90deg, #8b0000, #c62828)",
              WebkitBackgroundClip: "text",
              color: "transparent"
            }}
          >
            Contact Zolang Nexora Softwares
          </Typography>

          <Typography mt={2} color="text.secondary">
            Corporate inquiries, legal communication, and global partnerships
          </Typography>
          <Typography
            sx={{
              mt: 1.5,
              fontSize: "0.9rem",
              color: "rgba(255,255,255,0.7)",
              fontStyle: "italic",
              textAlign: "center",
              letterSpacing: "0.04em"
            }}
          >
            “யாதும் ஊரே யாவரும் கேளிர்”
          </Typography>

          <Typography
            sx={{
              mt: 0.4,
              fontSize: "0.75rem",
              color: "rgba(255,255,255,0.55)",
              textAlign: "center"
            }}
          >
            “Every place is our home; everyone is our kin.”
          </Typography>

        </Box>
      </Fade>

      <Grid container spacing={5}>
        {/* CONTACT DETAILS */}
        <Grid item xs={12} md={6}>
          <Slide in={visible} direction="right">
            <Card
              sx={{
                borderRadius: 4,
                border: "1px solid rgba(139,0,0,0.25)",
                animation: `${softPulse} 4s infinite`
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h5" fontWeight={700} gutterBottom>
                  Corporate & Regional Offices
                </Typography>

                <Divider sx={{ mb: 3 }} />

                {/* LEGAL OFFICE */}
                <Box display="flex" gap={2} mb={3}>
                  <Business color="error" />
                  <Box>
                    <Typography fontWeight={600}>
                      Registered Legal Office (India)
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Zolang Nexora Softwares
                      <br />
                      #1/149, Main Road
                      <br />
                      24, Villiyanallur
                      <br />
                      Mayiladuthurai – 609203
                      <br />
                      Tamil Nadu, India
                    </Typography>
                  </Box>
                </Box>

                {/* TORONTO OFFICE */}
                <Box display="flex" gap={2} mb={3}>
                  <LocationOn color="error" />
                  <Box>
                    <Typography fontWeight={600}>
                      North America – Regional Office
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Toronto, Ontario
                      <br />
                      Postal Code: M5V 3L9
                      <br />
                      Canada
                    </Typography>
                  </Box>
                </Box>

                {/* CONTACT */}
                <Box display="flex" gap={2} mb={2}>
                  <Email color="error" />
                  <Typography variant="body2">
                    contact@zolangnexora.com <br />
                    inquiry.zolang@gmail.com
                  </Typography>
                </Box>

                <Box display="flex" gap={2} mb={2}>
                  <Phone color="error" />
                  <Typography variant="body2">
                    +91 63825 85971<br />
                    +1 416 832 7635
                  </Typography>
                </Box>

                <Box display="flex" gap={2}>
                  <Language color="error" />
                  <Typography variant="body2">
                    www.zolangnexora.com
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Slide>
        </Grid>

        {/* CONTACT FORM */}
        <Grid item xs={12} md={6}>
          <Slide in={visible} direction="left">
            <Card
              sx={{
                borderRadius: 4,
                border: "1px solid rgba(139,0,0,0.25)"
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h5" fontWeight={700} gutterBottom>
                  Official Communication
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  mb={3}
                >
                  For legal, partnership, or enterprise-level discussions
                </Typography>

                <form onSubmit={handleSubmit}>
                  <TextField
                    fullWidth
                    label="Full Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    sx={{ mb: 3 }}
                  />

                  <TextField
                    fullWidth
                    label="Business Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    sx={{ mb: 3 }}
                  />

                  <TextField
                    fullWidth
                    label="Message"
                    name="message"
                    multiline
                    rows={4}
                    value={formData.message}
                    onChange={handleChange}
                    required
                    sx={{ mb: 4 }}
                  />

                  <Button
                    type="submit"
                    variant="contained"
                    endIcon={<Send />}
                    sx={{
                      background:
                        "linear-gradient(45deg, #8b0000, #b71c1c)",
                      px: 4,
                      py: 1.4,
                      borderRadius: 3,
                      fontWeight: 600
                    }}
                  >
                    Submit Inquiry
                  </Button>
                </form>
              </CardContent>
            </Card>
          </Slide>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Contact;
